
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { PantryItem, StorageLocation, PantryCategory, Language, FoodItem } from '../types';
import { t } from '../locales';
import { Box, Refrigerator, ThermometerSnowflake, Sun } from 'lucide-react';

interface SaveProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: { name: string; barcode: string; ingredients: FoodItem[]; categoriesTags?: string[] };
  onSave: (name: string, location: StorageLocation, category: PantryCategory) => void;
  language: Language;
}

const SaveProductModal: React.FC<SaveProductModalProps> = ({ 
    isOpen, onClose, product, onSave, language 
}) => {
  const [name, setName] = useState(product.name);
  const [location, setLocation] = useState<StorageLocation>('pantry');
  const [category, setCategory] = useState<PantryCategory>('misc');

  // Translated config
  const locationConfig = useMemo(() => {
      const c: Record<StorageLocation, { label: string, icon: React.ReactNode, categories: { id: PantryCategory, label: string, icon: string }[] }> = {
        pantry: {
            label: t(language, 'loc_pantry'),
            icon: <Box size={24} />,
            categories: [
                { id: 'canned', label: t(language, 'cat_canned'), icon: "🥫" },
                { id: 'dry', label: t(language, 'cat_dry'), icon: "🍝" },
                { id: 'snacks', label: t(language, 'cat_snacks'), icon: "🍪" },
                { id: 'sauces', label: t(language, 'cat_sauces'), icon: "🧂" },
                { id: 'jars', label: t(language, 'cat_jars'), icon: "🍯" },
                { id: 'beverages', label: t(language, 'cat_beverages'), icon: "☕" },
                { id: 'bakery', label: t(language, 'cat_bakery'), icon: "🍞" },
                { id: 'misc', label: t(language, 'cat_misc'), icon: "📦" }
            ]
        },
        fridge: {
            label: t(language, 'loc_fridge'),
            icon: <Refrigerator size={24} />,
            categories: [
                { id: 'dairy', label: t(language, 'cat_dairy'), icon: "🥛" },
                { id: 'meats', label: t(language, 'cat_meats'), icon: "🍗" },
                { id: 'fresh-meals', label: t(language, 'cat_fresh_meals'), icon: "🥗" },
                { id: 'spreads', label: t(language, 'cat_spreads'), icon: "🧈" },
                { id: 'chilled-drinks', label: t(language, 'cat_chilled_drinks'), icon: "🧃" },
                { id: 'desserts', label: t(language, 'cat_desserts'), icon: "🍰" }
            ]
        },
        freezer: {
            label: t(language, 'loc_freezer'),
            icon: <ThermometerSnowflake size={24} />,
            categories: [
                { id: 'frozen-meals', label: t(language, 'cat_frozen_meals'), icon: "🍕" },
                { id: 'frozen-produce', label: t(language, 'cat_frozen_produce'), icon: "🥦" },
                { id: 'frozen-meats', label: t(language, 'cat_frozen_meats'), icon: "🍖" },
                { id: 'ice-cream', label: t(language, 'cat_ice_cream'), icon: "🍦" },
                { id: 'frozen-snacks', label: t(language, 'cat_frozen_snacks'), icon: "🥔" }
            ]
        },
        counter: {
            label: t(language, 'loc_counter'),
            icon: <Sun size={24} />,
            categories: [
                { id: 'on-the-go', label: t(language, 'cat_on_the_go'), icon: "🍫" },
                { id: 'ambient-drinks', label: t(language, 'cat_ambient_drinks'), icon: "🧃" },
                { id: 'fresh-bakery', label: t(language, 'cat_fresh_bakery'), icon: "🍞" }
            ]
        }
      };
      return c;
  }, [language]);

  // Auto-suggest logic based on tags
  useEffect(() => {
      if (isOpen && product) {
          setName(product.name);
          const tags = (product.categoriesTags || []).map(s => s.toLowerCase());
          
          let loc: StorageLocation = 'pantry';
          let cat: PantryCategory = 'misc';

          if (tags.some(k => k.includes('frozen') || k.includes('surgelé') || k.includes('glace'))) {
              loc = 'freezer';
              if (tags.some(k => k.includes('meat') || k.includes('fish'))) cat = 'frozen-meats';
              else if (tags.some(k => k.includes('ice cream'))) cat = 'ice-cream';
              else if (tags.some(k => k.includes('vegetable'))) cat = 'frozen-produce';
              else cat = 'frozen-meals';
          } else if (tags.some(k => k.includes('refrigerated') || k.includes('fresh') || k.includes('dairy') || k.includes('cheese') || k.includes('yogurt'))) {
              loc = 'fridge';
              if (tags.some(k => k.includes('dairy') || k.includes('cheese'))) cat = 'dairy';
              else if (tags.some(k => k.includes('meat') || k.includes('ham'))) cat = 'meats';
              else cat = 'fresh-meals';
          } else {
              // Pantry/Counter logic
              if (tags.some(k => k.includes('bread') && k.includes('fresh'))) { loc = 'counter'; cat = 'fresh-bakery'; }
              else if (tags.some(k => k.includes('canned'))) { loc = 'pantry'; cat = 'canned'; }
              else if (tags.some(k => k.includes('pasta') || k.includes('rice'))) { loc = 'pantry'; cat = 'dry'; }
              else if (tags.some(k => k.includes('sauce') || k.includes('oil'))) { loc = 'pantry'; cat = 'sauces'; }
              else if (tags.some(k => k.includes('cookie') || k.includes('snack') || k.includes('candy'))) { loc = 'pantry'; cat = 'snacks'; }
              else if (tags.some(k => k.includes('beverage') || k.includes('drink') || k.includes('juice'))) { loc = 'pantry'; cat = 'beverages'; }
          }
          
          setLocation(loc);
          setCategory(cat);
      }
  }, [isOpen, product]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-popIn max-h-[90vh] overflow-y-auto border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">{t(language, 'saveToPantry')}</h3>
            <p className="text-xs text-gray-500 mb-4">{t(language, 'weFound')} <strong>{product.name}</strong></p>
            
            {/* Name Input */}
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t(language, 'nameThisItem')}</label>
            <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white mb-4 focus:ring-2 focus:ring-orange-500 outline-none transition-all" 
                autoFocus
            />
            
            {/* Location Selector */}
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Location</label>
            <div className="grid grid-cols-4 gap-2 mb-4">
                {(Object.keys(locationConfig) as StorageLocation[]).map(loc => (
                    <button 
                        key={loc} 
                        onClick={() => { 
                            setLocation(loc); 
                            // Default to first category when switching location
                            setCategory(locationConfig[loc].categories[0].id); 
                        }}
                        className={`py-2 px-1 rounded-lg text-[9px] font-bold uppercase transition-all flex flex-col items-center gap-1 ${location === loc ? 'bg-orange-500 text-white shadow-md scale-105' : 'bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}
                    >
                        <span className="text-lg">{locationConfig[loc].icon}</span>
                        {locationConfig[loc].label}
                    </button>
                ))}
            </div>

            {/* Category Selector */}
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Category</label>
            <div className="grid grid-cols-3 gap-2 mb-6">
                {locationConfig[location].categories.map(cat => (
                    <button 
                        key={cat.id} 
                        onClick={() => setCategory(cat.id)}
                        className={`py-2 px-1.5 rounded-lg text-[10px] font-bold uppercase transition-all flex items-center justify-center gap-1 ${category === cat.id ? 'bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                        <span>{cat.icon}</span>
                        <span className="truncate">{cat.label}</span>
                    </button>
                ))}
            </div>

            <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl font-bold text-gray-600 dark:text-gray-300 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600">{t(language, 'cancel')}</button>
                <button onClick={() => onSave(name, location, category)} className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 rounded-xl font-bold text-white shadow-lg transition-transform active:scale-95">{t(language, 'save')}</button>
            </div>
        </div>
    </div>,
    document.body
  );
};

export default SaveProductModal;
